const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * Derive a 16-byte AES-128 key from a string input.
 * SHA-256(input) → take first 16 bytes.
 * Must match backend GameService.deriveKey and Android CryptoUtils.deriveKey.
 */
export async function deriveKey(input: string): Promise<Uint8Array> {
  const data = encoder.encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hash).slice(0, 16);
}

/**
 * Decrypt AES-128-CBC ciphertext.
 * Format: Base64(IV[16] + ciphertext)
 * Must match backend encrypt() and Android CryptoUtils.decrypt().
 */
export async function decrypt(ciphertextB64: string, keyBytes: Uint8Array): Promise<string> {
  const raw = Uint8Array.from(atob(ciphertextB64), c => c.charCodeAt(0));
  const iv = raw.slice(0, 16);
  const ciphertext = raw.slice(16);

  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes.buffer as ArrayBuffer,
    { name: 'AES-CBC' },
    false,
    ['decrypt']
  );

  const plainBuffer = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv: iv.buffer as ArrayBuffer },
    key,
    ciphertext.buffer as ArrayBuffer
  );

  return decoder.decode(plainBuffer);
}

/**
 * Decode a Base64-encoded key.
 */
export function keyFromBase64(encoded: string): Uint8Array {
  return Uint8Array.from(atob(encoded), c => c.charCodeAt(0));
}

/**
 * Derive the initial key for the first location: SHA-256(gameCode + salt) → take(16)
 */
export async function deriveInitialKey(gameCode: string, salt: string): Promise<Uint8Array> {
  return deriveKey(gameCode + salt);
}

/**
 * Derive the next key in the progressive chain from a BCrypt answer hash.
 */
export async function deriveNextKey(answerHash: string): Promise<Uint8Array> {
  return deriveKey(answerHash);
}
